import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { SEO } from "@/components/SEO";
import { Star, Heart, Shield, Zap, Droplets, Thermometer, Moon, Leaf, Sparkles, Crown, Award, ArrowRight } from "lucide-react";
import ImageLightbox from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ProminentCTA } from "@/components/ui/prominent-cta";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import novaSeriesProduct from "@/assets/nova-series-product.jpg";
import novaSeriesProductMain from "@/assets/nova-series-product-main.jpg";
import novaSeriesProductAngle from "@/assets/nova-series-product-angle.jpg";
import novaSeriesProductSeat from "@/assets/nova-series-product-seat.jpg";
import novaSeriesLifestyle from "@/assets/nova-series-lifestyle.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
const Nova = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop(); // Scroll to top when page loads

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const features = [
    {
      icon: Crown,
      title: "Ultra-Premium Design",
      description: "Architectural elegance with premium materials and meticulous craftsmanship"
    },
    {
      icon: Zap,
      title: "Intelligent Automation",
      description: "Advanced AI-powered sensors for seamless, touch-free operation"
    },
    {
      icon: Droplets,
      title: "Precision Cleansing",
      description: "Multi-directional spray technology with customizable pressure and positioning"
    },
    {
      icon: Thermometer,
      title: "Climate Control",
      description: "Instantaneous heating system with precision temperature regulation"
    },
    {
      icon: Moon,
      title: "Ambient Experience",
      description: "Integrated mood lighting with customizable color temperatures"
    },
    {
      icon: Shield,
      title: "Advanced Hygiene",
      description: "Self-cleaning nozzles with UV sterilization and antimicrobial coating"
    },
    {
      icon: Sparkles,
      title: "Air Purification",
      description: "Built-in deodorizer with plasma ion technology for pristine air quality"
    },
    {
      icon: Leaf,
      title: "Eco-Intelligence",
      description: "Smart water conservation with eco-mode and energy optimization"
    }
  ];

  const specifications = [
    { label: "Dimensions", value: "705 × 420 × 540 mm" },
    { label: "Weight", value: "42 kg" },
    { label: "Water Consumption", value: "≤ 4.8L (Eco Mode)" },
    { label: "Power Rating", value: "1400W" },
    { label: "Water Pressure", value: "0.07 - 0.75 MPa" },
    { label: "Installation", value: "Floor-mounted" },
    { label: "Certification", value: "IPX4, CE, FCC" },
    { label: "Warranty", value: "3 Years Comprehensive" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/98 to-accent/5">
      <SEO
        title="NOVA TEG5 Smart Toilet | Family-Friendly Designer Bidet"
        description="NOVA TEG5 smart toilet with family-friendly dual seat, instant heating, motion sensors and UV sterilization. CE certified, installed across the EU."
        keywords="NOVA TEG5 smart toilet, architectural bidet design, family friendly smart toilet, dual seat bidet, designer luxury toilet, premium bathroom fixtures, smart toilet installation cost, NOVA vs LUXA comparison, European luxury bidet, buy NOVA toilet, architectural bathroom design, ultra-luxury bidet systems Marbella, architectural bathroom fixtures Spain, luxury smart toilets Costa del Sol, high-end toilet installation Andalusia, smart bathroom technology Europe"
        url="/products/nova"
        image="https://www.aureum7trading.com/images/products/nova-series-product-main.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "NOVA TEG5 Smart Toilet",
          "description": "Ultra-premium smart toilet with architectural elegance and intelligent automation featuring AI-powered sensors, climate control, and UV sterilization",
          "brand": {
            "@type": "Brand",
            "name": "Aureum VII Trading",
            "logo": "https://www.aureum7trading.com/lovable-uploads/aureum-vii-trading-logo.png"
          },
          "manufacturer": {
            "@type": "Organization",
            "name": "Aureum VII Trading",
            "url": "https://www.aureum7trading.com"
          },
          "image": [
            "https://www.aureum7trading.com/images/products/nova-series-product-main.jpg",
            "https://www.aureum7trading.com/images/products/nova-series-product-angle.jpg",
            "https://www.aureum7trading.com/images/products/nova-series-lifestyle.jpg"
          ],
          "sku": "NOVA-TEG5",
          "mpn": "TEG5",
          "gtin13": "8001234567892",
          "productID": "NOVA-TEG5",
          "category": "Smart Toilets > Luxury Bathroom Fixtures",
          "material": "Premium Ceramic, Antimicrobial Composite",
          "color": "White",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "EUR",
            "price": 600,
            "priceValidUntil": "2026-12-31",
            "itemCondition": "https://schema.org/NewCondition",
            "url": "https://www.aureum7trading.com/products/nova",
            "seller": {
              "@type": "Organization",
              "name": "Aureum VII Trading",
              "url": "https://www.aureum7trading.com"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "EUR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 3,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 5,
                  "maxValue": 14,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "@id": "https://www.aureum7trading.com/return-policy",
              "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 14,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn",
              "itemCondition": "https://schema.org/NewCondition",
              "refundType": "https://schema.org/FullRefund"
            }
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Dimensions",
              "value": "705 × 420 × 540 mm"
            },
            {
              "@type": "PropertyValue",
              "name": "Power Rating",
              "value": "1400W"
            },
            {
              "@type": "PropertyValue",
              "name": "Water Consumption",
              "value": "≤ 4.8L (Eco Mode)"
            },
            {
              "@type": "PropertyValue",
              "name": "Weight",
              "value": "42kg"
            },
            {
              "@type": "PropertyValue",
              "name": "Warranty",
              "value": "5 years comprehensive"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "NOVA TEG5", url: "/products/nova" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="container mx-auto px-4">
          <BreadcrumbNav items={[
            { label: "Home", href: "/" },
            { label: "Smart Toilets", href: "/smart-toilets" },
            { label: "NOVA TEG5" }
          ]} />
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-6">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge variant="outline" className="text-primary border-primary/30 bg-primary/5">
                  <Star className="w-3 h-3 mr-1" />
                  Flagship Collection
                </Badge>
                <h1 className="text-5xl lg:text-6xl font-light text-foreground leading-tight">
                  NOVA
                  <span className="block text-2xl lg:text-3xl text-primary font-medium mt-1">
                    Model: TEG5
                  </span>
                  <span className="block text-3xl lg:text-4xl text-muted-foreground font-extralight mt-2">
                    Smart Toilet
                  </span>
                </h1>
                <PriceFrom price={600} />
                <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl">
                  Experience the pinnacle of luxury smart toilet design with the NOVA TEG5. This premium intelligent sanitary system represents the ultimate fusion of architectural elegance and cutting-edge automation. Our luxury smart toilet combines AI-powered sensors, precision climate control, and advanced hygiene technology to deliver an unparalleled bathroom experience for the most discerning luxury living spaces.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:from-primary/90 hover:to-primary/70">
                  Request Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" size="lg" className="border-primary/30 text-primary hover:bg-primary/5">
                  Download Specifications
                </Button>
              </div>

              {/* PDF Download Button */}
              <PDFDownloadButton 
                productName="NOVA"
                className="mt-8"
              />

              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Premium Collection
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  3-Year Warranty
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  Luxury Design
                </div>
              </div>
            </div>

            <div className="relative">
              <ImageLightbox
                src={novaSeriesProductMain}
                alt="NOVA TEG5 luxury smart toilet with premium architectural design and intelligent automation features"
                title="NOVA Premium Design"
                className="rounded-2xl shadow-2xl overflow-hidden"
                showZoomIcon={true}
              >
                  <img loading="lazy" decoding="async"
                    src={novaSeriesProductMain}
                    alt="NOVA TEG5 architectural luxury smart toilet featuring ultra-premium materials, cutting-edge AI-powered automation, precision climate control technology, sophisticated modern design language, and intelligent sensor systems for ultimate comfort"
                    className="w-full h-auto transition-transform duration-700"
                  />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                  <span className="text-sm font-medium text-foreground">AVT × NOVA</span>
                </div>
              </ImageLightbox>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gradient-to-b from-background/50 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-light text-foreground">Intelligent Luxury Features</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Every detail meticulously crafted to deliver an extraordinary experience that transcends traditional expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {features.map((feature, index) => (
              <article key={index} className="group hover:shadow-xl transition-all duration-500 bg-card/50 backdrop-blur-sm rounded-lg border border-border/50 p-6 text-center space-y-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-light text-foreground">Design Excellence</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Witness the perfect harmony of form and function in every angle.
            </p>
          </div>

          <ProductGallery
            images={[
              { src: novaSeriesProductAngle, alt: "NOVA Smart Toilet - Angle view", title: "Architectural Profile" },
              { src: novaSeriesProductSeat, alt: "NOVA Smart Toilet - Seat detail", title: "Comfort Engineering" },
              { src: novaSeriesLifestyle, alt: "NOVA Smart Toilet - Lifestyle setting", title: "Luxury Living" },
            ]}
            className="max-w-4xl mx-auto"
          />
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-gradient-to-b from-background/50 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl font-light text-foreground">Technical Excellence</h2>
              <p className="text-xl text-muted-foreground">
                Precision engineering meets luxury design standards.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-card/50 backdrop-blur-sm rounded-lg border border-border/50 p-8 space-y-4">
                <h3 className="text-2xl font-semibold text-foreground mb-6">Product Specifications</h3>
                {specifications.map((spec, index) => (
                  <div key={index} className="flex justify-between items-center py-2">
                    <span className="text-muted-foreground">{spec.label}</span>
                    <span className="font-medium text-foreground">{spec.value}</span>
                  </div>
                ))}
              </div>

              <div className="bg-card/50 backdrop-blur-sm rounded-lg border border-border/50 p-8">
                <h3 className="text-2xl font-semibold text-foreground mb-6">Advanced Functions</h3>
                <div className="space-y-4 text-muted-foreground">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Auto-sensing lid and seat operation with proximity detection</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Dual-nozzle cleansing system with massage and oscillation</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Instant water heating with precision temperature control</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Warm air drying with adjustable intensity and positioning</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Integrated deodorizer with plasma ion technology</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Energy-efficient eco-mode with smart scheduling</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/5 via-primary/10 to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-light text-foreground">
              Transform Your Space with NOVA
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Experience the ultimate in luxury sanitary design. Contact our specialists to discover 
              how NOVA can elevate your project to unprecedented levels of sophistication.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:from-primary/90 hover:to-primary/70">
                <LocalizedLink to="/contact">
                  Schedule Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </LocalizedLink>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary/30 text-primary hover:bg-primary/5">
                <a href="mailto:info@aureum7trading.com?subject=NOVA%20TEG5%20Pricing%20Request">
                  Request Pricing
                </a>
              </Button>
            </div>

            <Separator className="max-w-xs mx-auto opacity-30" />
            
            <p className="text-sm text-muted-foreground italic">
              "Where architectural vision meets uncompromising luxury"
            </p>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <RelatedProducts currentProduct="NOVA" />

      {/* Prominent CTA before footer */}
      <ProminentCTA
        title="Transform Your Space with NOVA"
        description="Experience the ultimate in architectural luxury design. Contact our specialists to discover how NOVA can elevate your project with intelligent automation and sophisticated wellness features."
        primaryCTA={{ text: "Schedule Consultation", href: "/contact" }}
        secondaryCTA={{ text: "View All Collections", href: "/smart-toilets" }}
        showContactButtons={true}
        variant="gradient"
      />

      {/* Social Share Section */}
      <section className="py-12 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-4xl">
        <div className="text-center mb-8">
          <h2 className="font-playfair text-2xl font-medium text-primary mb-3">Share NOVA TEG5</h2>
          <p className="font-inter text-muted-foreground">Help others discover architectural luxury smart toilet design</p>
        </div>
        <SocialShare 
          url="https://www.aureum7trading.com/products/nova"
          title="NOVA TEG5 Ultra-Premium Luxury Smart Toilet - Aureum VII Trading"
        />
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-b from-white to-slate-50/30 container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="font-playfair text-3xl md:text-4xl font-medium text-primary mb-4">Request NOVA TEG5 Consultation</h2>
          <p className="font-inter text-lg text-muted-foreground max-w-2xl mx-auto">
            Get architectural luxury consultation, premium pricing, and professional installation guidance for your high-end bathroom project in Marbella, Spain, or across Europe.
          </p>
        </div>
        <div className="max-w-2xl mx-auto">
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
        >
          <ArrowRight className="w-4 h-4 rotate-[-90deg] group-hover:scale-110 transition-transform" />
        </button>
      )}
    </div>
  );
};

export default Nova;